import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { Player } from "@backstory/player";
import type { PanelActions, PanelFetchers } from "@backstory/player";
import type { CallSummary, Issue, IssueCluster, RawFrame, ServerLog, SymbolicatedFrame, TraceTree } from "@backstory/protocol";
import { request } from "../../api";
import { useToast } from "../../components/ui";

const enc = encodeURIComponent;

/** Fetchers for side data the panels ask for. Each returns [] / throws on missing backend so panels degrade. */
export function usePanelFetchers(sessionId: string): PanelFetchers {
  return useMemo<PanelFetchers>(
    () => ({
      symbolicate: (release: string | undefined, frames: RawFrame[]) =>
        request<{ frames: SymbolicatedFrame[] }>("/v1/symbolicate", { method: "POST", body: { release, frames } }).then((r) => r.frames),
      traces: () => request<TraceTree[] | { items: TraceTree[] }>(`/v1/sessions/${enc(sessionId)}/traces`).then((r) => (Array.isArray(r) ? r : r.items ?? [])),
      logs: () => request<{ items: ServerLog[] }>(`/v1/sessions/${enc(sessionId)}/logs?limit=2000`).then((r) => r.items ?? []),
      issues: () => request<Issue[] | { items: Issue[] }>(`/v1/sessions/${enc(sessionId)}/issues`).then((r) => (Array.isArray(r) ? r : r.items ?? [])),
      clusters: (ids: string[]) => Promise.all(ids.map((id) => request<IssueCluster>(`/v1/issues/${enc(id)}`).catch(() => null))).then((cs) => cs.filter((c): c is IssueCluster => !!c)),
      calls: () => request<CallSummary[] | { items: CallSummary[] }>(`/v1/sessions/${enc(sessionId)}/calls`).then((r) => (Array.isArray(r) ? r : (r.items ?? []))),
    }),
    [sessionId],
  );
}

/** Actions panels can perform against the Player and the app. */
export function usePanelActions(playerRef: { current: Player | null }, startTs: number): PanelActions {
  const navigate = useNavigate();
  const toast = useToast();
  return useMemo<PanelActions>(
    () => ({
      seek: (absMs) => void playerRef.current?.seek(absMs - startTs),
      highlightNode: (id, opts) => playerRef.current?.highlightNode(id, opts),
      drawBoxes: (boxes, opts) => playerRef.current?.drawBoxes(boxes, opts),
      findSessionsFor: (selector) => void navigate({ to: "/", search: { selector } as never }),
      copy: (text) => {
        void navigator.clipboard?.writeText(text).then(
          () => toast.push("success", "Copied to clipboard."),
          () => toast.push("error", "Clipboard unavailable."),
        );
      },
      openCluster: (clusterId) => void navigate({ to: "/issues/$id" as never, params: { id: clusterId } as never }),
    }),
    [playerRef, startTs, navigate, toast],
  );
}
